import React, { useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import Select from "react-select";
import dayjs from "dayjs";
import { MdClose, MdOutlineOpenInNew, MdUnfoldMore } from "react-icons/md";
import Sidebar from "../Sidebar";
import Layout from "../../layout/Layout";
import OrderDetailsModal from "../AdminBookings/OrderDetailsModal";
import { GET_BOOKINGS } from "../AdminBookings/graphql";
import { GET_EMPLOYEE_OPS_REPORT } from "./graphql";
import FeatureUsage from "./FeatureUsage";

// How the professionals use the app on a booking: holds, how late "Start
// service" is pressed against the slot, service timer, product list,
// inventory scan and running-late notices. Booking date, IST.

const PRESETS = [
  { key: "today", label: "Today" },
  { key: "last7", label: "7 days" },
  { key: "last30", label: "30 days" },
  { key: "last90", label: "90 days" },
  { key: "custom", label: "Custom" },
];

// A professional with fewer completed bookings than this is greyed out in
// the "things to look at" list - one bad day isn't a pattern.
const MIN_BOOKINGS = 5;

const ratio = (a, b) => (b ? a / b : null);
const pct = (a, b) => (b ? `${Math.round((a / b) * 100)}%` : "—");
const minsLabel = (v) => {
  if (v === null || v === undefined) return "—";
  const r = Math.round(v);
  if (Math.abs(r) >= 90) return `${r < 0 ? "-" : ""}${(Math.abs(r) / 60).toFixed(1)} hr`;
  return `${r} min`;
};
const metres = (v) => (v === null || v === undefined ? "—" : v >= 1000 ? `${(v / 1000).toFixed(1)} km` : `${Math.round(v)} m`);
const timeIST = (v) => (v ? dayjs(v).format("h:mm A") : "—");
const slotLabel = (s) => (s ? dayjs(`2000-01-01 ${s}`).format("h:mm A") : "—");

// Green when the share is healthy, amber when it needs a look, red when it
// needs action. goodWhenHigh=false flips it for shares where less is better.
const shareTone = (value, { good = 0.8, ok = 0.5, goodWhenHigh = true } = {}) => {
  if (value === null || value === undefined) return "text-gray-400";
  const v = goodWhenHigh ? value : 1 - value;
  const g = goodWhenHigh ? good : 1 - good;
  const o = goodWhenHigh ? ok : 1 - ok;
  if (v >= g) return "text-emerald-600";
  if (v >= o) return "text-amber-600";
  return "text-red-600";
};
const delayTone = (m, onTime) => {
  if (m === null || m === undefined) return "text-gray-400";
  if (m <= onTime) return "text-emerald-600";
  if (m <= 30) return "text-amber-600";
  return "text-red-600";
};

const Card = ({ title, hint, right, children, className = "" }) => (
  <section className={`bg-white rounded-xl border border-gray-100 shadow-sm ${className}`}>
    {(title || right) && (
      <header className="flex flex-wrap items-start justify-between gap-2 px-4 pt-4">
        <div>
          <h2 className="font-semibold text-gray-900">{title}</h2>
          {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
        </div>
        {right}
      </header>
    )}
    <div className="p-4">{children}</div>
  </section>
);

const Kpi = ({ label, value, sub, valueClass = "text-gray-900" }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
    <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
    <p className={`text-3xl font-semibold mt-1 tabular-nums ${valueClass}`}>{value}</p>
    {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
  </div>
);

// Horizontal bars, one per bucket, shared by the delay histogram and the
// app-step funnel.
const Bars = ({ rows, total, colorFor }) => {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="space-y-1.5">
      {rows.map((r) => (
        <div key={r.key} className="flex items-center gap-2 text-sm">
          <span className="w-28 shrink-0 text-gray-600">{r.label}</span>
          <span className="flex-1 h-3 rounded-full bg-gray-100 overflow-hidden">
            <span className={`block h-full rounded-full ${colorFor(r)}`} style={{ width: `${(r.count / max) * 100}%` }} />
          </span>
          <span className="w-24 text-right text-gray-800 tabular-nums">
            {r.count} <span className="text-gray-400 text-xs">{pct(r.count, total)}</span>
          </span>
        </div>
      ))}
    </div>
  );
};

const BUCKET_COLOR = {
  EARLY: "bg-emerald-500",
  ON_TIME: "bg-emerald-400",
  L20: "bg-yellow-400",
  L30: "bg-amber-400",
  L60: "bg-orange-500",
  L120: "bg-red-500",
  L120P: "bg-red-700",
};

// Median start delay per slot hour: are the morning slots the late ones?
const HourStrip = ({ rows, onTime }) => {
  if (!rows.length) return <p className="text-sm text-gray-400">No started bookings in this period.</p>;
  const max = Math.max(onTime * 2, ...rows.map((r) => Math.max(0, r.medianDelay || 0)));
  return (
    <div className="overflow-x-auto">
      <div className="flex items-end gap-1.5 h-40 min-w-max pt-4">
        {rows.map((r) => {
          const m = Math.max(0, r.medianDelay || 0);
          const color = m <= onTime ? "bg-emerald-400" : m <= 30 ? "bg-amber-400" : "bg-red-500";
          return (
            <div
              key={r.hour}
              className="flex flex-col items-center justify-end h-full w-11"
              title={`${r.starts} bookings · ${r.late} late · median ${minsLabel(r.medianDelay)} · avg ${minsLabel(r.avgDelay)}`}
            >
              <span className="text-[10px] text-gray-600 tabular-nums mb-0.5">{Math.round(r.medianDelay || 0)}m</span>
              <span className={`w-7 rounded-t ${color}`} style={{ height: `${Math.max(3, (m / max) * 100)}%` }} />
              <span className="text-[10px] text-gray-500 mt-1">{dayjs().hour(r.hour).format("h A")}</span>
              <span className="text-[10px] text-gray-400">{r.starts}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Columns of the per-professional table. `sort` returns the value used to
// order the table; `cell` draws it.
const columns = (t) => [
  {
    key: "completed",
    label: "Done",
    title: "Completed bookings (of all assigned)",
    sort: (e) => e.completed,
    cell: (e) => (
      <span>
        {e.completed}
        <span className="text-gray-400 text-xs"> / {e.assigned}</span>
      </span>
    ),
  },
  {
    key: "holds",
    label: "Holds",
    title: "Bookings this professional marked 'customer not responding'. Released = grace window lapsed and the booking moved to OnHold.",
    sort: (e) => e.holdsByEmployee,
    cell: (e) => (
      <span className={ratio(e.holdsByEmployee, e.assigned) > 0.15 ? "text-red-600 font-semibold" : ""}>
        {e.holdsByEmployee}
        {e.holdsReleased > 0 && <span className="text-xs text-gray-500"> · {e.holdsReleased} rel.</span>}
        {e.holdsByAdmin > 0 && <span className="text-xs text-gray-400"> · +{e.holdsByAdmin} office</span>}
      </span>
    ),
  },
  {
    key: "median",
    label: "Start vs slot",
    title: "Median minutes between the slot time and 'Start service'",
    sort: (e) => (e.medianStartDelay === null ? -1e9 : e.medianStartDelay),
    cell: (e) => <span className={`font-semibold ${delayTone(e.medianStartDelay, t.onTimeMinutes)}`}>{minsLabel(e.medianStartDelay)}</span>,
  },
  {
    key: "ontime",
    label: "On time",
    title: `Started within ${t.onTimeMinutes} min of the slot`,
    sort: (e) => ratio(e.startsOnTime, e.startsTimed) ?? -1,
    cell: (e) => <span className={shareTone(ratio(e.startsOnTime, e.startsTimed), { good: 0.7, ok: 0.4 })}>{pct(e.startsOnTime, e.startsTimed)}</span>,
  },
  {
    key: "verylate",
    label: `>${t.veryLateMinutes}m late`,
    title: `Started more than ${t.veryLateMinutes} min after the slot`,
    sort: (e) => e.startsVeryLate,
    cell: (e) => <span className={e.startsVeryLate ? "text-red-600" : "text-gray-400"}>{e.startsVeryLate}</span>,
  },
  {
    key: "why",
    label: "Reach · prep",
    title: "Median minutes late reaching the customer, and median minutes from 'Reached' to 'Start service'",
    sort: (e) => e.medianReachDelay ?? -1e9,
    cell: (e) => (
      <span className="text-xs text-gray-700 whitespace-nowrap">
        <span className={delayTone(e.medianReachDelay, t.onTimeMinutes)}>{minsLabel(e.medianReachDelay)}</span>
        <span className="text-gray-400"> · </span>
        {minsLabel(e.medianReachToStart)}
      </span>
    ),
  },
  {
    key: "silent",
    label: "Silent late",
    title: "Late starts where nobody sent the customer a running-late notice",
    sort: (e) => e.silentLate,
    cell: (e) => <span className={e.silentLate ? "text-red-600" : "text-gray-400"}>{e.silentLate}</span>,
  },
  {
    key: "timer",
    label: "Timer",
    title: "Services that need the timer where it was actually started (customer confirmed)",
    sort: (e) => ratio(e.timerLinesStarted, e.timerLines) ?? -1,
    cell: (e) =>
      e.timerLines ? (
        <span className={shareTone(ratio(e.timerLinesStarted, e.timerLines))}>
          {pct(e.timerLinesStarted, e.timerLines)}
          <span className="text-xs text-gray-400"> {e.timerLinesStarted}/{e.timerLines}</span>
        </span>
      ) : (
        <span className="text-gray-300">n/a</span>
      ),
  },
  {
    key: "product",
    label: "Product list",
    title: "Completed bookings where the product list was sent to the customer before starting",
    sort: (e) => ratio(e.productListSent, e.completed) ?? -1,
    cell: (e) => (
      <span className={shareTone(ratio(e.productListSent, e.completed))}>
        {pct(e.productListSent, e.completed)}
        {e.productOverridden > 0 && <span className="text-xs text-gray-400"> · {e.productOverridden} skip</span>}
      </span>
    ),
  },
  {
    key: "scan",
    label: "Scan",
    title: "Completed bookings with the inventory scan done (force = scanned without a real scan)",
    sort: (e) => ratio(e.scanned - e.forceScanned, e.completed) ?? -1,
    cell: (e) => (
      <span className={shareTone(ratio(e.scanned - e.forceScanned, e.completed))}>
        {pct(e.scanned, e.completed)}
        {e.forceScanned > 0 && <span className="text-xs text-gray-400"> · {e.forceScanned} force</span>}
      </span>
    ),
  },
  {
    key: "flow",
    label: "Full flow",
    title: "Completed bookings with every step tapped: confirmed, headed, reached, started, finished",
    sort: (e) => ratio(e.fullFlow, e.completed) ?? -1,
    cell: (e) => <span className={shareTone(ratio(e.fullFlow, e.completed), { good: 0.9, ok: 0.6 })}>{pct(e.fullFlow, e.completed)}</span>,
  },
  {
    key: "gps",
    label: "GPS",
    title: `Phone location at Reached / Start. Far = more than ${t.farMetres} m from the customer's pin at both. Fake = location came from a fake-GPS app. No loc = GPS off or permission denied.`,
    sort: (e) => (e.locMocked * 1000 + e.locFar * 10 + e.locNoFix),
    cell: (e) => {
      if (!e.locWithFix && !e.locNoFix) return <span className="text-gray-300" title={e.locOldApp ? "Old app build - no location sent" : ""}>{e.locOldApp ? "old app" : "—"}</span>;
      return (
        <span className="text-xs">
          {e.locMocked > 0 && <span className="px-1 py-0.5 rounded bg-red-600 text-white mr-1">{e.locMocked} fake</span>}
          <span className={e.locFar ? "text-red-600 font-semibold" : "text-emerald-600"}>{e.locFar} far</span>
          <span className="text-gray-400"> / {e.locMeasured}</span>
          {e.locNoFix > 0 && <span className="text-amber-600"> · {e.locNoFix} no loc</span>}
        </span>
      );
    },
  },
  {
    key: "burst",
    label: "Tapped late",
    title: `Steps tapped together after the fact: reached within ${t.burstTravelSeconds}s of headed, or finished within ${t.burstServiceMinutes} min of start`,
    sort: (e) => ratio(e.burstTaps, e.completed) ?? -1,
    cell: (e) => <span className={shareTone(ratio(e.burstTaps, e.completed), { good: 0.1, ok: 0.3, goodWhenHigh: false })}>{e.burstTaps ? pct(e.burstTaps, e.completed) : "0"}</span>,
  },
  {
    key: "late",
    label: "Late msgs",
    title: "Running-late notices and check-in nudges sent from the professional's app",
    sort: (e) => e.runningLateSent,
    cell: (e) => (
      <span className="text-gray-700">
        {e.runningLateSent}
        <span className="text-xs text-gray-400"> · {e.nudgesSent} nudge</span>
      </span>
    ),
  },
];

// Plain-language list of what stands out, worst first. Only professionals
// with enough bookings to mean something.
const buildFindings = (s, employees, t) => {
  const out = [];
  const enough = employees.filter((e) => e.completed >= MIN_BOOKINGS);
  const names = (list) => list.slice(0, 4).map((e) => e.employeeName || "Unknown").join(", ") + (list.length > 4 ? ` +${list.length - 4} more` : "");

  const veryLate = enough.filter((e) => e.medianStartDelay > t.veryLateMinutes).sort((a, b) => b.medianStartDelay - a.medianStartDelay);
  if (veryLate.length) out.push({ tone: "red", text: `Usually start more than ${t.veryLateMinutes} min after the slot: ${names(veryLate)}.` });

  if (s.startsLate && s.silentLate) {
    out.push({ tone: s.silentLate / s.startsLate > 0.5 ? "red" : "amber", text: `${s.silentLate} of ${s.startsLate} late starts (${pct(s.silentLate, s.startsLate)}) had no running-late message to the customer.` });
  }

  if (s.medianReachDelay !== null && s.medianReachToStart !== null && s.medianStartDelay > t.onTimeMinutes) {
    out.push({
      tone: "amber",
      text:
        s.medianReachToStart > Math.max(0, s.medianReachDelay)
          ? `Lateness is mostly after arriving: median ${minsLabel(s.medianReachToStart)} between "Reached" and "Start service" (arrival is ${minsLabel(s.medianReachDelay)} vs slot). Product list approval and setup are worth checking.`
          : `Lateness is mostly arriving late: median arrival ${minsLabel(s.medianReachDelay)} after the slot, then ${minsLabel(s.medianReachToStart)} to start.`,
    });
  }

  const timerUsers = employees.filter((e) => e.timerLines > 0);
  const timerNonUsers = timerUsers.filter((e) => e.timerLinesStarted === 0 && e.timerLines >= 2).sort((a, b) => b.timerLines - a.timerLines);
  if (timerNonUsers.length) out.push({ tone: "red", text: `Never started the service timer despite timer services: ${names(timerNonUsers)}.` });
  if (s.timerNeverConfirmed) out.push({ tone: "amber", text: `${s.timerNeverConfirmed} timer starts were requested but the customer never confirmed. Check whether the push/WhatsApp confirmation is reaching customers.` });

  const holders = enough.filter((e) => ratio(e.holdsByEmployee, e.assigned) > 0.15).sort((a, b) => ratio(b.holdsByEmployee, b.assigned) - ratio(a.holdsByEmployee, a.assigned));
  if (holders.length) out.push({ tone: "amber", text: `Put more than 15% of their bookings on hold: ${names(holders)}. Listen to a few of these calls; holds free up their day.` });

  const bursty = enough.filter((e) => ratio(e.burstTaps, e.completed) > 0.3);
  if (bursty.length) out.push({ tone: "red", text: `Tap the booking steps together afterwards instead of live, so their timings can't be trusted: ${names(bursty)}.` });

  const noProduct = enough.filter((e) => ratio(e.productListSent, e.completed) < 0.5);
  if (noProduct.length) out.push({ tone: "amber", text: `Sent the product list on fewer than half their bookings: ${names(noProduct)}.` });

  const forcey = enough.filter((e) => ratio(e.forceScanned, e.scanned) > 0.3);
  if (forcey.length) out.push({ tone: "amber", text: `Force-scan inventory on more than 30% of their bookings: ${names(forcey)}.` });

  const faked = employees.filter((e) => e.locMocked > 0);
  if (faked.length) out.push({ tone: "red", text: `Used a fake-GPS app when marking Reached / Start: ${names(faked)}.` });
  const far = employees.filter((e) => e.locFar > 0).sort((a, b) => b.locFar - a.locFar);
  if (far.length) out.push({ tone: "red", text: `Marked Reached / Start more than ${t.farMetres} m from the customer: ${names(far)}. See the "Not at customer" list below.` });
  const noLoc = enough.filter((e) => ratio(e.locNoFix, e.completed) > 0.3);
  if (noLoc.length) out.push({ tone: "amber", text: `Keep GPS off or location permission denied on most bookings: ${names(noLoc)}.` });

  if (!out.length) out.push({ tone: "green", text: "Nothing stands out in this period." });
  return out;
};

const TONE = {
  red: "bg-red-50 text-red-800 border-red-100",
  amber: "bg-amber-50 text-amber-900 border-amber-100",
  green: "bg-emerald-50 text-emerald-800 border-emerald-100",
};

const HOLD_STATUS = {
  AWAITING_RESPONSE: { label: "Waiting", cls: "bg-amber-50 text-amber-700" },
  RESPONDED: { label: "Customer responded", cls: "bg-emerald-50 text-emerald-700" },
  UNASSIGNED_STALE: { label: "Released", cls: "bg-red-50 text-red-700" },
};

const selectStyles = {
  control: (b) => ({ ...b, minHeight: 38, borderColor: "#e5e7eb", boxShadow: "none" }),
  multiValue: (b) => ({ ...b, backgroundColor: "#FBB033" }),
};

const EmployeeOpsReport = () => {
  const [preset, setPreset] = useState("last30");
  const [custom, setCustom] = useState({
    start: dayjs().subtract(29, "day").format("YYYY-MM-DD"),
    end: dayjs().format("YYYY-MM-DD"),
  });
  const [employeeIds, setEmployeeIds] = useState([]);
  const [sort, setSort] = useState({ key: "median", dir: -1 });
  const [listTab, setListTab] = useState("late");
  const [fetchBooking, { loading: openingBooking }] = useMutation(GET_BOOKINGS);
  const [viewingBooking, setViewingBooking] = useState(null);
  const [openError, setOpenError] = useState("");

  const range = useMemo(() => {
    const today = dayjs().format("YYYY-MM-DD");
    if (preset === "today") return { startDate: today, endDate: today };
    if (preset === "last7") return { startDate: dayjs().subtract(6, "day").format("YYYY-MM-DD"), endDate: today };
    if (preset === "last90") return { startDate: dayjs().subtract(89, "day").format("YYYY-MM-DD"), endDate: today };
    if (preset === "custom") return { startDate: custom.start, endDate: custom.end };
    return { startDate: dayjs().subtract(29, "day").format("YYYY-MM-DD"), endDate: today };
  }, [preset, custom]);

  // Everyone (feeds the table and the professional picker), and the same
  // period narrowed to the picked professionals (feeds everything else).
  const allQ = useQuery(GET_EMPLOYEE_OPS_REPORT, { variables: { filter: range }, fetchPolicy: "cache-and-network" });
  const selQ = useQuery(GET_EMPLOYEE_OPS_REPORT, {
    variables: { filter: { ...range, employeeIds } },
    skip: employeeIds.length === 0,
    fetchPolicy: "cache-and-network",
  });
  const allRes = allQ.data?.getEmployeeOpsReport;
  const activeRes = (employeeIds.length ? selQ : allQ).data?.getEmployeeOpsReport;
  const all = allRes?.data;
  const report = activeRes?.data;
  const s = report?.summary;
  const t = report?.thresholds || all?.thresholds;
  const errorMessage = allQ.error?.message || selQ.error?.message || (allRes?.status === "ERROR" && allRes.message) || (activeRes?.status === "ERROR" && activeRes.message);
  const loadingFirst = (allQ.loading && !allQ.data) || (employeeIds.length > 0 && selQ.loading && !selQ.data);

  const employeeOptions = useMemo(
    () => (all?.employees || []).map((e) => ({ value: String(e.employeeId), label: e.employeeName || "Unknown" })).sort((a, b) => a.label.localeCompare(b.label)),
    [all]
  );

  const cols = useMemo(() => (t ? columns(t) : []), [t]);
  const rows = useMemo(() => {
    const list = (report?.employees || []).slice();
    const col = cols.find((c) => c.key === sort.key);
    if (col) list.sort((a, b) => (col.sort(a) - col.sort(b)) * sort.dir || b.completed - a.completed);
    return list;
  }, [report, cols, sort]);

  const findings = useMemo(() => (s && t ? buildFindings(s, report.employees, t) : []), [s, t, report]);

  const timerTeam = useMemo(() => {
    const list = report?.employees || [];
    const eligible = list.filter((e) => e.timerLines > 0);
    return { eligible: eligible.length, using: eligible.filter((e) => e.timerLinesStarted > 0).length };
  }, [report]);

  const openBooking = async (r) => {
    setOpenError("");
    try {
      const res = await fetchBooking({ variables: { data: { id: r.orderId, bookingId: r.bookingId } } });
      const order = (res.data?.getBookings?.orders || []).find((o) => String(o.id) === String(r.orderId));
      if (!order) throw new Error(res.data?.getBookings?.message || "Booking not found");
      setViewingBooking(order);
    } catch (e) {
      setOpenError(`Could not open the booking: ${e.message}`);
    }
  };

  const toggleSort = (key) => setSort((cur) => (cur.key === key ? { key, dir: -cur.dir } : { key, dir: -1 }));
  const pickEmployee = (id) => setEmployeeIds([String(id)]);

  const steps = s
    ? [
        { key: "confirmed", label: "Confirmed", count: s.stepConfirmed },
        { key: "headed", label: "Headed", count: s.stepHeaded },
        { key: "reached", label: "Reached", count: s.stepReached },
        { key: "started", label: "Started", count: s.stepStarted },
        { key: "finished", label: "Finished", count: s.stepFinished },
      ]
    : [];

  return (
    <div className="min-h-screen flex">
      <Sidebar />
      <Layout>
        <div className="max-w-7xl space-y-5 pb-16">
          <div>
            <h1 className="text-2xl font-semibold text-white">Employee activity</h1>
            <p className="text-sm text-gray-400 mt-0.5">
              Whether professionals use the app the way it's meant to be used on a booking: holds, start time vs slot, service timer, product list, scan and running-late messages. By booking date (IST).
            </p>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3 flex flex-wrap items-center gap-3">
            <div className="flex rounded-lg bg-gray-100 p-0.5">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setPreset(p.key)}
                  className={`px-3 py-1.5 rounded-md text-sm ${preset === p.key ? "bg-white shadow-sm font-semibold text-gray-900" : "text-gray-600 hover:text-gray-900"}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {preset === "custom" && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <input type="date" value={custom.start} max={custom.end} onChange={(e) => e.target.value && setCustom((c) => ({ ...c, start: e.target.value }))} className="border border-gray-200 rounded-lg px-2 py-1.5" />
                to
                <input type="date" value={custom.end} min={custom.start} onChange={(e) => e.target.value && setCustom((c) => ({ ...c, end: e.target.value }))} className="border border-gray-200 rounded-lg px-2 py-1.5" />
              </div>
            )}
            <div className="min-w-[240px] flex-1 max-w-md">
              <Select
                isMulti
                placeholder="All professionals"
                options={employeeOptions}
                value={employeeOptions.filter((o) => employeeIds.includes(o.value))}
                onChange={(sel) => setEmployeeIds(sel ? sel.map((x) => x.value) : [])}
                styles={selectStyles}
              />
            </div>
            <span className="text-xs text-gray-400 ml-auto">
              {dayjs(range.startDate).format("D MMM YYYY")} – {dayjs(range.endDate).format("D MMM YYYY")}
            </span>
          </div>

          {errorMessage && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">Could not load the report: {errorMessage}</div>}
          {openError && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">{openError}</div>}
          {loadingFirst && <div className="py-16 text-center text-gray-400">Loading…</div>}

          {!loadingFirst && s && t && (
            <>
              {employeeIds.length > 0 && (
                <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 text-sm text-amber-900">
                  <span>
                    Showing <strong>{employeeOptions.filter((o) => employeeIds.includes(o.value)).map((o) => o.label).join(", ")}</strong> only
                  </span>
                  <button type="button" onClick={() => setEmployeeIds([])} className="inline-flex items-center gap-1 font-medium hover:underline">
                    <MdClose /> Show everyone
                  </button>
                </div>
              )}

              {/* Headline numbers */}
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
                <Kpi
                  label="Put on hold"
                  value={s.holdsTotal}
                  valueClass={ratio(s.holdsTotal, s.assigned) > 0.1 ? "text-red-600" : "text-gray-900"}
                  sub={`${pct(s.holdsTotal, s.assigned)} of ${s.assigned} · ${s.holdsByEmployee} by pros · ${s.holdsByAdmin} by office`}
                />
                <Kpi
                  label="Hold outcome"
                  value={`${s.holdsResponded}/${s.holdsReleased}`}
                  sub={`responded / released · ${s.holdsCompleted} still completed`}
                />
                <Kpi
                  label="Start vs slot"
                  value={minsLabel(s.medianStartDelay)}
                  valueClass={delayTone(s.medianStartDelay, t.onTimeMinutes)}
                  sub={`median · average ${minsLabel(s.avgStartDelay)} · ${s.startsTimed} bookings`}
                />
                <Kpi
                  label="Started on time"
                  value={pct(s.startsOnTime, s.startsTimed)}
                  valueClass={shareTone(ratio(s.startsOnTime, s.startsTimed), { good: 0.7, ok: 0.4 })}
                  sub={`within ${t.onTimeMinutes} min · ${s.startsVeryLate} more than ${t.veryLateMinutes} min late`}
                />
                <Kpi
                  label="Service timer used"
                  value={pct(s.timerLinesStarted, s.timerLines)}
                  valueClass={shareTone(ratio(s.timerLinesStarted, s.timerLines))}
                  sub={`${s.timerLinesStarted}/${s.timerLines} services · ${timerTeam.using} of ${timerTeam.eligible} pros use it`}
                />
                <Kpi
                  label="Silent late"
                  value={s.silentLate}
                  valueClass={s.silentLate ? "text-red-600" : "text-gray-900"}
                  sub={`late starts with no running-late message (${pct(s.silentLate, s.startsLate)})`}
                />
              </div>

              {/* What stands out */}
              <Card title="Things to look at" hint={`Professionals with fewer than ${MIN_BOOKINGS} completed bookings are left out.`}>
                <ul className="space-y-2">
                  {findings.map((f, i) => (
                    <li key={i} className={`text-sm rounded-lg border px-3 py-2 ${TONE[f.tone]}`}>
                      {f.text}
                    </li>
                  ))}
                </ul>
              </Card>

              {/* When Start service gets pressed */}
              <div className="grid lg:grid-cols-2 gap-5">
                <Card title="When is “Start service” pressed?" hint="Minutes after the booking's slot time. Completed bookings only.">
                  <Bars rows={report.delayBuckets} total={s.startsTimed} colorFor={(r) => BUCKET_COLOR[r.key] || "bg-gray-400"} />
                  <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                    <div className="rounded-lg bg-gray-50 p-2">
                      <p className="text-[11px] uppercase text-gray-500">Reach vs slot</p>
                      <p className={`font-semibold ${delayTone(s.medianReachDelay, t.onTimeMinutes)}`}>{minsLabel(s.medianReachDelay)}</p>
                    </div>
                    <div className="rounded-lg bg-gray-50 p-2">
                      <p className="text-[11px] uppercase text-gray-500">Reach → start</p>
                      <p className="font-semibold text-gray-800">{minsLabel(s.medianReachToStart)}</p>
                    </div>
                    <div className="rounded-lg bg-gray-50 p-2">
                      <p className="text-[11px] uppercase text-gray-500">Start vs slot</p>
                      <p className={`font-semibold ${delayTone(s.medianStartDelay, t.onTimeMinutes)}`}>{minsLabel(s.medianStartDelay)}</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">Medians. Tells you whether lateness comes from arriving late or from the time between arriving and starting.</p>
                </Card>

                <Card title="By slot time" hint="Median start delay for each slot hour. Number under the bar = bookings.">
                  <HourStrip rows={report.byHour} onTime={t.onTimeMinutes} />
                </Card>
              </div>

              <div className="grid lg:grid-cols-2 gap-5">
                <Card title="App steps tapped" hint={`Of ${s.completed} completed bookings.`}>
                  <Bars rows={steps} total={s.completed} colorFor={() => "bg-[#FBB033]"} />
                  <p className="text-sm text-gray-700 mt-3">
                    Every step tapped: <strong>{pct(s.fullFlow, s.completed)}</strong>
                    <span className="text-gray-400"> · </span>
                    Tapped together afterwards: <strong className={s.burstTaps ? "text-red-600" : ""}>{s.burstTaps}</strong>
                  </p>
                </Card>
                <Card title="Other features" hint="Completed bookings.">
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                    <div>
                      <dt className="text-gray-500">Product list sent</dt>
                      <dd className="font-semibold">{pct(s.productListSent, s.completed)} <span className="text-xs text-gray-400 font-normal">{s.productListApproved} approved · {s.productOverridden} skipped</span></dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Inventory scanned</dt>
                      <dd className="font-semibold">{pct(s.scanned, s.completed)} <span className="text-xs text-gray-400 font-normal">{s.forceScanned} force-scanned</span></dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Timer bookings</dt>
                      <dd className="font-semibold">{s.timerOrdersUsed}/{s.timerOrders} <span className="text-xs text-gray-400 font-normal">with the timer used</span></dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Timer issues</dt>
                      <dd className="font-semibold">{s.timerNeverConfirmed} <span className="text-xs text-gray-400 font-normal">never confirmed · {s.timerEarlyEnd} early-end asks · {s.timerViaWhatsapp} via WhatsApp</span></dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Running-late messages</dt>
                      <dd className="font-semibold">{s.runningLateSent} <span className="text-xs text-gray-400 font-normal">from pros' app</span></dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Check-in nudges</dt>
                      <dd className="font-semibold">{s.nudgesSent} <span className="text-xs text-gray-400 font-normal">from pros' app</span></dd>
                    </div>
                  </dl>
                </Card>
              </div>

              {/* Per professional */}
              <Card title="By professional" hint="Click a column to sort, a name to see only them. Hover a heading for what it counts.">
                <div className="overflow-x-auto -mx-4">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                        <th className="px-4 py-2 font-medium sticky left-0 bg-white">Professional</th>
                        {cols.map((c) => (
                          <th key={c.key} title={c.title} className="px-3 py-2 font-medium whitespace-nowrap">
                            <button type="button" onClick={() => toggleSort(c.key)} className={`inline-flex items-center gap-0.5 hover:text-gray-900 ${sort.key === c.key ? "text-gray-900" : ""}`}>
                              {c.label}
                              {sort.key === c.key ? <span>{sort.dir < 0 ? "↓" : "↑"}</span> : <MdUnfoldMore className="text-gray-300" />}
                            </button>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((e) => (
                        <tr key={e.employeeId} className={`border-b border-gray-50 hover:bg-gray-50 ${e.completed < MIN_BOOKINGS ? "opacity-60" : ""}`}>
                          <td className="px-4 py-2 sticky left-0 bg-white">
                            <button type="button" onClick={() => pickEmployee(e.employeeId)} className="font-medium text-gray-900 hover:underline text-left whitespace-nowrap">
                              {e.employeeName || "Unknown"}
                            </button>
                          </td>
                          {cols.map((c) => (
                            <td key={c.key} className="px-3 py-2 tabular-nums whitespace-nowrap">
                              {c.cell(e)}
                            </td>
                          ))}
                        </tr>
                      ))}
                      {!rows.length && (
                        <tr>
                          <td colSpan={cols.length + 1} className="px-4 py-8 text-center text-gray-400">No bookings in this period.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>

              {/* Everything else in the app, history included */}
              <FeatureUsage usage={report?.featureUsage} onPick={pickEmployee} />

              {/* Booking lists */}
              <Card
                title={listTab === "late" ? "Late starts" : listTab === "far" ? "Not at the customer" : "Bookings put on hold"}
                hint={
                  listTab === "late"
                    ? `Started more than ${t.onTimeMinutes} min after the slot, worst first (up to 150).`
                    : listTab === "far"
                      ? `Phone more than ${t.farMetres} m from the customer's pin at both Reached and Start, or a fake-GPS app. Straight-line distance.`
                      : "Most recent first (up to 150)."
                }
                right={
                  <div className="flex rounded-lg bg-gray-100 p-0.5">
                    {[
                      { key: "late", label: `Late starts (${s.startsLate})` },
                      { key: "holds", label: `Holds (${s.holdsTotal})` },
                      { key: "far", label: `Not at customer (${report.farFromCustomer.length})` },
                    ].map((x) => (
                      <button key={x.key} type="button" onClick={() => setListTab(x.key)} className={`px-3 py-1.5 rounded-md text-sm ${listTab === x.key ? "bg-white shadow-sm font-semibold text-gray-900" : "text-gray-600"}`}>
                        {x.label}
                      </button>
                    ))}
                  </div>
                }
              >
                <div className="overflow-x-auto -mx-4">
                  {listTab === "far" ? (
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                          {["Booking", "Day", "Slot", "Professional", "At Reached", "At Start", "GPS accuracy", ""].map((h) => (
                            <th key={h} className="px-3 py-2 font-medium whitespace-nowrap first:pl-4">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {report.farFromCustomer.map((r) => (
                          <tr key={r.orderId} className="border-b border-gray-50 hover:bg-gray-50">
                            <td className="px-3 pl-4 py-2 font-mono text-xs">{r.bookingId}</td>
                            <td className="px-3 py-2 whitespace-nowrap">{dayjs(r.bookingDay).format("D MMM")}</td>
                            <td className="px-3 py-2 whitespace-nowrap">{slotLabel(r.timeSlot)}</td>
                            <td className="px-3 py-2">{r.employeeName}</td>
                            <td className={`px-3 py-2 tabular-nums ${r.reachDistance > t.farMetres ? "text-red-600 font-semibold" : ""}`}>{metres(r.reachDistance)}</td>
                            <td className={`px-3 py-2 tabular-nums ${r.startDistance > t.farMetres ? "text-red-600 font-semibold" : ""}`}>{metres(r.startDistance)}</td>
                            <td className="px-3 py-2 text-gray-500">
                              {r.accuracy !== null ? `±${Math.round(r.accuracy)} m` : "—"}
                              {r.mocked && <span className="ml-2 px-1.5 py-0.5 rounded bg-red-600 text-white text-[11px]">Fake GPS</span>}
                            </td>
                            <td className="px-3 py-2">
                              <button type="button" disabled={openingBooking} onClick={() => openBooking(r)} className="text-gray-400 hover:text-gray-900" title="Open booking">
                                <MdOutlineOpenInNew />
                              </button>
                            </td>
                          </tr>
                        ))}
                        {!report.farFromCustomer.length && (
                          <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">Nobody was far from the customer in this period (bookings tapped from the new app only).</td></tr>
                        )}
                      </tbody>
                    </table>
                  ) : listTab === "late" ? (
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                          {["Booking", "Day", "Professional", "Slot", "Reached", "Started", "Late by", "Customer told?", ""].map((h) => (
                            <th key={h} className="px-3 py-2 font-medium whitespace-nowrap first:pl-4">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {report.lateStarts.map((r) => (
                          <tr key={r.orderId} className="border-b border-gray-50 hover:bg-gray-50">
                            <td className="px-3 pl-4 py-2 font-mono text-xs">{r.bookingId}</td>
                            <td className="px-3 py-2 whitespace-nowrap">{dayjs(r.bookingDay).format("D MMM")}</td>
                            <td className="px-3 py-2">{r.employeeName}</td>
                            <td className="px-3 py-2 whitespace-nowrap">{slotLabel(r.timeSlot)}</td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {timeIST(r.reachedAt)}
                              {r.reachDelay !== null && <span className={`text-xs ml-1 ${delayTone(r.reachDelay, t.onTimeMinutes)}`}>({minsLabel(r.reachDelay)})</span>}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">{timeIST(r.startedAt)}</td>
                            <td className={`px-3 py-2 font-semibold whitespace-nowrap ${delayTone(r.startDelay, t.onTimeMinutes)}`}>{minsLabel(r.startDelay)}</td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {r.runningLateSent ? <span className="text-emerald-700">Yes, {timeIST(r.firstLateNoticeAt)}</span> : <span className="text-red-600">No</span>}
                            </td>
                            <td className="px-3 py-2">
                              <button type="button" disabled={openingBooking} onClick={() => openBooking(r)} className="text-gray-400 hover:text-gray-900" title="Open booking">
                                <MdOutlineOpenInNew />
                              </button>
                            </td>
                          </tr>
                        ))}
                        {!report.lateStarts.length && (
                          <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">No late starts in this period.</td></tr>
                        )}
                      </tbody>
                    </table>
                  ) : (
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                          {["Booking", "Day", "Slot", "Professional", "Marked by", "Marked at", "Outcome", "Booking now", ""].map((h) => (
                            <th key={h} className="px-3 py-2 font-medium whitespace-nowrap first:pl-4">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {report.holds.map((r) => {
                          const st = HOLD_STATUS[r.holdStatus] || { label: r.holdStatus, cls: "bg-gray-100 text-gray-600" };
                          return (
                            <tr key={r.orderId} className="border-b border-gray-50 hover:bg-gray-50">
                              <td className="px-3 pl-4 py-2 font-mono text-xs">{r.bookingId}</td>
                              <td className="px-3 py-2 whitespace-nowrap">{dayjs(r.bookingDay).format("D MMM")}</td>
                              <td className="px-3 py-2 whitespace-nowrap">{slotLabel(r.timeSlot)}</td>
                              <td className="px-3 py-2">{r.employeeName}</td>
                              <td className="px-3 py-2">
                                {r.holdStartedByType === "ADMIN" ? "Office" : r.holdStartedByType === "EMPLOYEE" ? "Professional" : "—"}
                                {r.holdStartedByName && <span className="text-xs text-gray-500"> · {r.holdStartedByName}</span>}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                {timeIST(r.holdStartedAt)}
                                {r.minutesFromSlot !== null && (
                                  <span className="text-xs text-gray-500 ml-1">
                                    ({r.minutesFromSlot < 0 ? `${minsLabel(-r.minutesFromSlot)} before slot` : `${minsLabel(r.minutesFromSlot)} after`})
                                  </span>
                                )}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                <span className={`px-1.5 py-0.5 rounded text-xs ${st.cls}`}>{st.label}</span>
                                {r.respondedAt && <span className="text-xs text-gray-500 ml-1">{timeIST(r.respondedAt)}</span>}
                              </td>
                              <td className="px-3 py-2">{r.status}</td>
                              <td className="px-3 py-2">
                                <button type="button" disabled={openingBooking} onClick={() => openBooking(r)} className="text-gray-400 hover:text-gray-900" title="Open booking">
                                  <MdOutlineOpenInNew />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                        {!report.holds.length && (
                          <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">No holds in this period.</td></tr>
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              </Card>
            </>
          )}
        </div>
      </Layout>
      {viewingBooking && <OrderDetailsModal booking={viewingBooking} onClose={() => setViewingBooking(null)} />}
    </div>
  );
};

export default EmployeeOpsReport;
